import { CheckCircle, AlertTriangle, XCircle, Activity } from 'lucide-react';
import { Badge } from '@/components/common';

interface HealthStatus {
  name: string;
  status: 'healthy' | 'warning' | 'error';
  message: string;
  lastCheck?: string;
}

interface SystemHealthProps {
  services: HealthStatus[];
  loading?: boolean;
}

export function SystemHealth({ services, loading = false }: SystemHealthProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="animate-pulse flex items-center gap-4">
            <div className="w-10 h-10 bg-secondary-200 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-secondary-200 rounded w-1/3" />
              <div className="h-3 bg-secondary-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Activity className="w-5 h-5 text-secondary-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      healthy: <Badge variant="success">Healthy</Badge>,
      warning: <Badge variant="warning">Warning</Badge>,
      error: <Badge variant="danger">Error</Badge>,
    };
    return badges[status as keyof typeof badges] || <Badge>Unknown</Badge>;
  };

  return (
    <div className="space-y-4">
      {services.map((service, index) => (
        <div
          key={index}
          className="flex items-center justify-between p-4 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className={`p-2 rounded-lg ${
              service.status === 'healthy' ? 'bg-green-100' :
              service.status === 'warning' ? 'bg-yellow-100' :
              'bg-red-100'
            }`}>
              {getStatusIcon(service.status)}
            </div>
            <div>
              <h4 className="font-medium text-secondary-900">{service.name}</h4>
              <p className="text-sm text-secondary-600 mt-0.5">{service.message}</p>
              {service.lastCheck && (
                <p className="text-xs text-secondary-500 mt-1">
                  Last checked: {service.lastCheck}
                </p>
              )}
            </div>
          </div>
          {getStatusBadge(service.status)}
        </div>
      ))}
    </div>
  );
}
